import type { CaseListCustomColumn } from '../hooks/useCaseListColumns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  LinearProgress,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Person } from '@mui/icons-material';
import { useQueryClient } from 'react-query';

import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';
import { CaseStatisticsModal } from './CaseStatisticsModal';
import { Link } from '@frontend/features/common/components/Link';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { routes } from '@frontend/router/routes';
import { ChipCustom } from '@frontend/features/common/components/ChipCustom';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { Dot } from '@frontend/features/common/components/Dot';
import { formatISOStringToFriendlyDate, getTimeSinceDate } from '@frontend/features/common/helper';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { useGetAllWorkflowsQuery, useGetWorkflowSteps } from '@frontend/features/workflows/queries';
import { useGetAllUsers } from '@frontend/features/users/queries';
import { useBulkUpdateCases } from '@frontend/features/cases/queries';
import { CaseBulkUpdateDto, CaseResponseDto } from '@bandido/shared/dtos';
import { CaseStatus } from '@bandido/shared/constants';

interface CasesListDesktopProps {
  customColumns?: CaseListCustomColumn[];
  title: string;
  subtitle: string;
  pagination: any;
  filtersCount: number;
  filtersOptions: any;
  onSearchChange: (value: string) => void;
  sorting: any;
  onCreate: () => void;
  actionToggles?: any[];
  statisticsButton: JSX.Element;
  isLoading: boolean;
  isRefreshing?: boolean;
  items: CaseResponseDto[];
  totalCount: number;
  isStatisticsModalOpen: boolean;
  onCloseStatistics: () => void;
}

export const CasesListDesktop = ({
  customColumns = [],
  title,
  subtitle,
  pagination,
  filtersCount,
  filtersOptions,
  onSearchChange,
  sorting,
  onCreate,
  actionToggles,
  statisticsButton,
  isLoading,
  isRefreshing = false,
  items,
  totalCount,
  isStatisticsModalOpen,
  onCloseStatistics,
}: CasesListDesktopProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkWorkflowId, setBulkWorkflowId] = useState<string>('');
  const [bulkStepId, setBulkStepId] = useState<string>('');
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const [bulkAgentValue, setBulkAgentValue] = useState<string>('');

  const resetBulkForm = useCallback(() => {
    setBulkWorkflowId('');
    setBulkStepId('');
    setBulkStatus('');
    setBulkAgentValue('');
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    resetBulkForm();
  }, [resetBulkForm]);

  const handleSearchChange = useCallback(
    (value: string) => {
      clearSelection();
      onSearchChange(value);
    },
    [clearSelection, onSearchChange],
  );

  const paginationWithClear = pagination
    ? {
        ...pagination,
        handlePagination: (value: number) => {
          clearSelection();
          pagination.handlePagination(value);
        },
        handleRowsPerPage: (value: number) => {
          clearSelection();
          pagination.handleRowsPerPage(value);
        },
      }
    : undefined;

  const sortingWithClear = sorting
    ? {
        ...sorting,
        handler: (options: any) => {
          clearSelection();
          sorting.handler(options);
        },
      }
    : undefined;

  const listSignature = useMemo(
    () =>
      JSON.stringify({
        page: pagination?.page,
        rowsPerPage: pagination?.rowsPerPage,
        sort: sorting?.options?.sort,
        order: sorting?.options?.order,
        filters: filtersOptions?.searchParams?.toString(),
      }),
    [
      pagination?.page,
      pagination?.rowsPerPage,
      sorting?.options?.sort,
      sorting?.options?.order,
      filtersOptions?.searchParams,
    ],
  );

  const prevSignatureRef = useRef(listSignature);

  useEffect(() => {
    if (prevSignatureRef.current !== listSignature) {
      prevSignatureRef.current = listSignature;
      if (selectedIds.length > 0) {
        clearSelection();
      }
    }
  }, [listSignature, selectedIds.length, clearSelection]);

  useEffect(() => {
    if (!bulkWorkflowId) {
      setBulkStepId('');
    }
  }, [bulkWorkflowId]);

  const { data: workflows } = useGetAllWorkflowsQuery({
    currentPage: 1,
    pageSize: 1000,
    search: '',
  });

  const { data: workflowSteps } = useGetWorkflowSteps(bulkWorkflowId || undefined);

  const { data: agents } = useGetAllUsers({
    currentPage: 1,
    pageSize: 1000,
    search: '',
  });

  const statusOptions = useMemo(
    () => [
      {
        id: CaseStatus.canceled,
        name: t(`cases.status.${CaseStatus.canceled}`),
      },
      {
        id: CaseStatus.done,
        name: t(`cases.status.${CaseStatus.done}`),
      },
      {
        id: CaseStatus.inProgress,
        name: t(`cases.status.${CaseStatus.inProgress}`),
      },
      {
        id: CaseStatus.needsAgentAction,
        name: t(`cases.status.${CaseStatus.needsAgentAction}`),
      },
      {
        id: CaseStatus.needsClientAction,
        name: t(`cases.status.${CaseStatus.needsClientAction}`),
      },
      {
        id: CaseStatus.open,
        name: t(`cases.status.${CaseStatus.open}`),
      },
    ],
    [t],
  );

  const { mutateAsync: bulkUpdateCases, isLoading: isBulkUpdating } = useBulkUpdateCases();

  // Desktop specific columns
  const desktopColumns = [
    {
      id: 'id',
      label: 'id',
      align: 'left' as const,
      isSticky: true,
      canSort: true,
      minWidth: 100,
      format: (data: string) => (
        <Link underline="hover" to={routes.dashboard.caseView.replace(':id', data)}>
          {addLeadingZeros(Number(data), defaultLeadingZeros)}
        </Link>
      ),
    },
    {
      id: 'clientFullName',
      minWidth: 140,
      isSticky: true,
      canSort: true,
      sortField: 'clientFirstName',
      label: t('cases.list.table.client'),
      align: 'left' as const,
    },
    {
      id: 'subject',
      canSort: true,
      sortField: 'name',
      label: t('cases.list.table.subject'),
      align: 'left' as const,
      maxWidth: 300,
      tooltip: true,
    },
    {
      id: 'status',
      canSort: true,
      label: t('cases.list.table.status'),
      align: 'left' as const,
    },
    {
      id: 'step',
      canSort: true,
      label: t('cases.list.table.step'),
      align: 'left' as const,
    },
    {
      id: 'agent',
      canSort: true,
      sortField: 'agentFirstName',
      label: t('cases.list.table.agent'),
      align: 'left' as const,
    },

    {
      id: 'team',
      canSort: true,
      sortField: 'teamName',
      label: t('cases.list.table.team'),
      align: 'left' as const,
      maxWidth: 300,
      tooltip: true,
    },
    {
      id: 'workflow',
      sortField: 'workflowName',
      canSort: true,
      label: t('cases.list.table.workflow'),
      align: 'left' as const,
      maxWidth: 300,
      tooltip: true,
    },
    {
      id: 'organization',
      canSort: true,
      sortField: 'organizationName',
      label: t('cases.list.table.organization'),
      align: 'left' as const,
    },
    {
      id: 'agencyName',
      canSort: true,
      label: t('cases.list.table.agency'),
      align: 'left' as const,
    },
    {
      id: 'channel',
      canSort: true,
      sortField: 'originatedByChannel',
      label: t('cases.list.table.channel'),
      align: 'left' as const,
    },
    {
      id: 'updatedSince',
      canSort: true,
      sortField: 'updatedAt',
      label: t('cases.list.table.lastUpdate'),
      align: 'left' as const,
    },
    {
      id: 'created',
      canSort: true,
      sortField: 'createdAt',
      label: t('cases.list.table.created'),
      align: 'left' as const,
    },
  ];

  const visibleColumns = [
    ...desktopColumns,
    ...customColumns.map(column => ({
      id: column.id, label: column.label, canSort: false, align: 'left' as const, minWidth: 160,
    })),
  ];

  // Transform raw data for desktop table view
  const tableData = {
    key: 'id',
    items: items.map((dbItem) => ({
      ...Object.fromEntries(customColumns.map(column => [column.id, column.render(dbItem.id)])),
      clientFullName: dbItem.clientFirstName ? (
        <Box display="flex" alignItems="center" gap={1}>
          <IconAvatar>
            <Person fontSize="small" htmlColor={theme.palette.common.white} />
          </IconAvatar>
          <Link underline="hover" to={routes.dashboard.clientView.replace(':id', String(dbItem.clientId))}>
            {`${dbItem.clientFirstName} ${dbItem.clientLastName}`}
          </Link>
        </Box>
      ) : (
        'N/A'
      ),
      id: dbItem.id,
      subject: dbItem.name,
      agencyName: dbItem.agencyName || t('common.unassigned'),
      team: dbItem.teamId ? (
        <Box display="flex" alignItems="center" gap={1}>
          <Dot color={dbItem.teamColor} />
          <Link
            underline="hover"
            to={`${routes.dashboard.agencyView.root.replace(
              ':id',
              String(dbItem.agencyId),
            )}/${routes.dashboard.agencyView.team.replace(':teamId', String(dbItem.teamId))}`}
          >
            {dbItem.teamName}
          </Link>
        </Box>
      ) : (
        t('common.unassigned')
      ),
      status: (
        <ChipCustom
          size="small"
          label={t(`cases.status.${dbItem.status}`)}
          backgroundColor={theme.palette.cases[dbItem.status].bg}
          textColor={theme.palette.cases[dbItem.status].text}
          component="span"
        />
      ),
      channel: dbItem.originatedByChannel ? t(`cases.originatedByChannel.${dbItem.originatedByChannel}`) : t('common.unassigned'),
      organization: dbItem.organizationName || '',
      workflow: dbItem.workflowName || t('common.unassigned'),
      step: dbItem.stepName || t('common.unassigned'),
      agent: dbItem.agentId ? (
        <Box display="flex" alignItems="center" gap={1}>
          <IconAvatar>
            <Person fontSize="small" htmlColor={theme.palette.common.white} />
          </IconAvatar>
          <Link underline="hover" to={routes.dashboard.userView.replace(':id', String(dbItem.agentId))}>
            {`${dbItem.agentFirstName} ${dbItem.agentLastName}`}
          </Link>
        </Box>
      ) : (
        'N/A'
      ),
      created: formatISOStringToFriendlyDate(dbItem.createdAt),
      updatedSince: getTimeSinceDate(dbItem.updatedAt),
    })),
    totalCount,
  };

  const hasBulkBar = selectedIds.length > 0 && !isRefreshing;
  const selectedCountLabel = t('cases.list.bulk.selectedCount', { count: selectedIds.length });
  const workflowOptions = workflows?.items || [];
  const stepOptions = workflowSteps || [];
  const agentOptions = agents?.items || [];
  const workflowLabelMap = new Map(workflowOptions.map((workflow) => [workflow.id, workflow.name]));
  const stepLabelMap = new Map(stepOptions.map((step) => [step.id, step.name]));
  const agentLabelMap = new Map(
    agentOptions.map((agent) => [agent.clientId, `${agent.firstName} ${agent.lastName}`.trim()]),
  );
  const statusLabelMap = new Map(statusOptions.map((status) => [status.id, status.name]));

  const handleApplyBulkUpdate = async () => {
    if (bulkStepId && !bulkWorkflowId) {
      addToast({
        message: t('cases.list.bulk.workflowRequired'),
        color: 'error',
      });
      return;
    }

    if (isRefreshing) return;

    const patch: CaseBulkUpdateDto['patch'] = {};

    if (bulkWorkflowId) {
      patch.workflowId = bulkWorkflowId;
    }

    if (bulkStepId) {
      patch.stepId = bulkStepId;
    }

    if (bulkStatus) {
      patch.status = bulkStatus;
    }

    if (bulkAgentValue === 'unassigned') {
      patch.agentId = null;
    } else if (bulkAgentValue !== '') {
      patch.agentId = bulkAgentValue;
    }

    if (Object.keys(patch).length === 0) {
      addToast({
        message: t('cases.list.bulk.noChanges'),
        color: 'error',
      });
      return;
    }

    const selectedCount = selectedIds.length;

    try {
      await bulkUpdateCases({
        caseIds: selectedIds,
        patch,
      });
      addToast({
        message: t('cases.list.bulk.updatedSuccess', { count: selectedCount }),
        color: 'success',
      });
      clearSelection();
      queryClient.invalidateQueries(QueryKeys.getAllCases);
    } catch (_error) {
      addToast({
        message: t('cases.list.bulk.updatedError'),
        color: 'error',
      });
    }
  };

  return (
    <ListLayout title={title} subtitle={subtitle}>
      <Box sx={{ pb: hasBulkBar ? theme.spacing(12) : 0 }}>
        {isRefreshing && <LinearProgress aria-label={t('cases.list.refreshing')} />}
        <ListContentLayout
          pagination={paginationWithClear}
          filtersCount={filtersCount}
          stickyHeader
          filters={filtersOptions}
          onSearchChange={handleSearchChange}
          sorting={sortingWithClear}
          onCreate={onCreate}
          actionToggles={actionToggles}
          statisticsButton={statisticsButton}
          columns={visibleColumns}
          isLoading={isLoading}
          data={tableData}
          rowSelection={{
            enabled: !isRefreshing,
            selectedIds,
            onChangeSelectedIds: (ids) => setSelectedIds(ids as number[]),
          }}
        />
      </Box>

      {hasBulkBar && (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.drawer + 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.common.white,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              px: 3,
              py: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
              <Typography variant="subtitle2">{t('cases.list.bulk.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCountLabel}
              </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel shrink>{t('cases.list.bulk.workflow')}</InputLabel>
              <Select
                value={bulkWorkflowId}
                label={t('cases.list.bulk.workflow')}
                displayEmpty
                onChange={(event) => setBulkWorkflowId(event.target.value)}
                renderValue={(value) =>
                  value ? workflowLabelMap.get(value) || String(value) : t('cases.list.bulk.leaveUnchanged')
                }
              >
                <MenuItem value="">{t('cases.list.bulk.leaveUnchanged')}</MenuItem>
                {workflowOptions.map((workflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }} disabled={!bulkWorkflowId}>
              <InputLabel shrink>{t('cases.list.bulk.step')}</InputLabel>
              <Select
                value={bulkStepId}
                label={t('cases.list.bulk.step')}
                displayEmpty
                onChange={(event) => setBulkStepId(event.target.value)}
                renderValue={(value) =>
                  value ? stepLabelMap.get(value) || String(value) : t('cases.list.bulk.leaveUnchanged')
                }
              >
                <MenuItem value="">{t('cases.list.bulk.leaveUnchanged')}</MenuItem>
                {stepOptions.map((step) => (
                  <MenuItem key={step.id} value={step.id}>
                    {step.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel shrink>{t('cases.list.bulk.status')}</InputLabel>
              <Select
                value={bulkStatus}
                label={t('cases.list.bulk.status')}
                displayEmpty
                onChange={(event) => setBulkStatus(event.target.value)}
                renderValue={(value) =>
                  value ? statusLabelMap.get(value) || String(value) : t('cases.list.bulk.leaveUnchanged')
                }
              >
                <MenuItem value="">{t('cases.list.bulk.leaveUnchanged')}</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel shrink>{t('cases.list.bulk.agent')}</InputLabel>
              <Select
                value={bulkAgentValue}
                label={t('cases.list.bulk.agent')}
                displayEmpty
                onChange={(event) => setBulkAgentValue(event.target.value)}
                renderValue={(value) => {
                  if (value === 'unassigned') {
                    return t('cases.list.bulk.unassigned');
                  }

                  return value ? agentLabelMap.get(String(value)) || String(value) : t('cases.list.bulk.leaveUnchanged');
                }}
              >
                <MenuItem value="">{t('cases.list.bulk.leaveUnchanged')}</MenuItem>
                <MenuItem value="unassigned">{t('cases.list.bulk.unassigned')}</MenuItem>
                {agentOptions.map((agent) => (
                  <MenuItem key={agent.clientId} value={agent.clientId}>
                    {`${agent.firstName} ${agent.lastName}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
              <Button variant="outlined" onClick={clearSelection}>
                {t('cases.list.bulk.clear')}
              </Button>
              <Button variant="contained" onClick={handleApplyBulkUpdate} disabled={isBulkUpdating}>
                {t('cases.list.bulk.apply')}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <CaseStatisticsModal 
        open={isStatisticsModalOpen} 
        onClose={onCloseStatistics} 
      />
    </ListLayout>
  );
};
